import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProductBySlug, urlFor } from '../lib/sanity';
import FacebookCTA from '../components/FacebookCTA';

function ProductDetails() {
  const { productName } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchProduct();
  }, [productName]);

  const fetchProduct = async () => {
    try {
      const data = await getProductBySlug(productName);
      setProduct(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#F4900C] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <Link to="/products" className="text-[#F4900C] hover:underline">
            ← Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50">
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 md:pt-40 md:pb-16">
        <div className="absolute top-10 left-10 w-96 h-96 bg-[#FED700]/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#F4900C]/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Product Image */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12">
                <img 
                  src={urlFor(product.mainImage).width(800).url()}
                  alt={product.name}
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>

            {/* Right - Product Info */}
            <div>
              <Link 
                to="/products" 
                className="inline-flex items-center text-[#F4900C] hover:text-[#F4900C]/80 font-semibold mb-4 transition-colors"
              >
                ← Back to Products
              </Link>

              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {product.name}
              </h1>

              {product.price && (
                <div className="text-4xl font-bold text-[#F4900C] mb-6">
                  ฿{product.price.toLocaleString()}
                </div>
              )}

              {/* Quick Info Grid */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {product.players && (
                  <div className="bg-white rounded-lg p-4 shadow-md">
                    <div className="text-3xl mb-2">👥</div>
                    <div className="text-sm text-gray-600">Players</div>
                    <div className="font-semibold text-gray-900">{product.players}</div>
                  </div>
                )}

                {product.duration && (
                  <div className="bg-white rounded-lg p-4 shadow-md">
                    <div className="text-3xl mb-2">⏱️</div>
                    <div className="text-sm text-gray-600">Duration</div>
                    <div className="font-semibold text-gray-900">{product.duration}</div>
                  </div>
                )}

                {product.age && (
                  <div className="bg-white rounded-lg p-4 shadow-md">
                    <div className="text-3xl mb-2">🎯</div>
                    <div className="text-sm text-gray-600">Recommended Age</div>
                    <div className="font-semibold text-gray-900">{product.age}</div>
                  </div>
                )}

                {product.category && (
                  <div className="bg-white rounded-lg p-4 shadow-md">
                    <div className="text-3xl mb-2">📚</div>
                    <div className="text-sm text-gray-600">Category</div>
                    <div className="font-semibold text-gray-900 capitalize">
                      {product.category.replace('-', ' ')}
                    </div>
                  </div>
                )}
              </div>

              {/* Learning Outcomes */}
              {product.learningOutcomes && product.learningOutcomes.length > 0 && (
                <div className="bg-gradient-to-r from-[#FED700]/10 to-[#F4900C]/10 rounded-lg p-6 mb-6">
                  <h3 className="font-bold text-gray-900 mb-3 text-lg">What You'll Learn</h3>
                  <ul className="space-y-2">
                    {product.learningOutcomes.map((outcome, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-[#F4900C] font-bold">✓</span>
                        <span className="text-gray-700">{outcome}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      {product.description && (
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                About This <span className="text-[#F4900C]">Product</span>
              </h2>
              
              <div className="prose prose-lg max-w-none text-gray-700 mb-12">
                {product.description.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="mb-6 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>

              {/* Gallery Images */}
              {product.galleryImages && product.galleryImages.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {product.galleryImages.map((image, idx) => (
                    <div 
                      key={idx}
                      onClick={() => setSelectedImage(urlFor(image).width(1200).url())}
                      className="relative aspect-square bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg overflow-hidden cursor-zoom-in hover:shadow-lg transition-all duration-300 group"
                    >
                      <img 
                        src={urlFor(image).width(400).url()}
                        alt={`${product.name} - Image ${idx + 1}`}
                        className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300 flex items-center justify-center">
                        <span className="text-white opacity-0 group-hover:opacity-100 text-2xl">🔍</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Components Section */}
      {product.components && product.components.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                What's <span className="text-[#F4900C]">Inside</span>
              </h2>
              
              <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-100">
                <div className="grid md:grid-cols-2 gap-4">
                  {product.components.map((component, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg"
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-[#FED700] to-[#F4900C] rounded-full flex items-center justify-center text-white font-bold">
                        {idx + 1}
                      </div>
                      <span className="text-gray-900 font-medium">{component}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Video Section */}
      {product.videoId && (
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                How to <span className="text-[#F4900C]">Play</span>
              </h2>
              
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${product.videoId}`}
                  title={`${product.name} - How to Play`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Where to Buy Section */}
      <FacebookCTA
        heading="Ready to Get Started?"
        message={`Contact us on Facebook to purchase ${product.name} and start your financial literacy journey today!`}
        buttonLabel="Order on Facebook"
        note="We typically respond within 24 hours • Fast shipping across Thailand"
      />

      {/* Image Zoom Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-6xl max-h-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white text-4xl hover:text-gray-300 transition-colors"
            >
              ×
            </button>
            <img 
              src={selectedImage}
              alt="Zoomed product"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductDetails;